import React from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  Button,
  Divider,
  Alert,
  Chip
} from '@mui/material';
import { Add as AddIcon, Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GroceryListItem from './GroceryListItem';

/**
 * Grocery list container component
 * Displays a list of grocery items with optional edit capabilities
 * 
 * @param {Object} props
 * @param {Array} props.items - List of grocery items
 * @param {string} props.title - List title
 * @param {boolean} props.editable - Whether items can be edited
 * @param {boolean} props.showActions - Show action buttons (add, save)
 * @param {Function} props.onEditItem - Edit item handler
 * @param {Function} props.onDeleteItem - Delete item handler
 * @param {Function} props.onAddItem - Add new item handler
 * @param {Function} props.onSaveList - Save entire list handler
 * @param {Function} props.onCancel - Cancel editing handler
 * @param {Function} props.onTogglePurchased - Toggle purchased status handler
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.isGeneratedList - Whether this is a generated (unsaved) list
 * @param {string} props.emptyMessage - Message to show when list is empty
 */
const GroceryListContainer = ({
  items = [],
  title,
  editable = false,
  showActions = false,
  onEditItem,
  onDeleteItem,
  onAddItem,
  onSaveList,
  onCancel,
  onTogglePurchased,
  loading = false,
  isGeneratedList = false,
  emptyMessage
}) => {
  const { t } = useTranslation();

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  // Define preferred category order for consistent display
  const preferredCategoryOrder = [
    'Produce',
    'Meat',
    'Dairy',
    'Bakery',
    'Pantry',
    'Frozen',
    'Beverages',
    'Household',
    'Other'
  ];

  // Extract actual categories from items and sort them
  const actualCategories = Object.keys(groupedItems);
  
  // Sort categories: first by preferred order, then alphabetically for any not in preferred list
  const orderedCategories = [
    ...preferredCategoryOrder.filter(cat => actualCategories.includes(cat)),
    ...actualCategories
      .filter(cat => !preferredCategoryOrder.includes(cat))
      .sort()
  ];

  return (
    <Paper 
      sx={{ 
        p: isGeneratedList ? 3 : 0,
        bgcolor: isGeneratedList ? '#f5f5f5' : 'white',
        border: isGeneratedList ? '2px solid #1976d2' : 'none'
      }}
    >
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        p={isGeneratedList ? 0 : 2}
        mb={isGeneratedList ? 2 : 0}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        <Box>
          <Typography variant="h6">
            {title || t('shopping.groceryList', 'Grocery List')}
          </Typography>
          {items.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {items.length} {t('shopping.items', 'items')}
              {items.filter(item => item.is_purchased).length > 0 && (
                <Chip 
                  label={`${items.filter(item => item.is_purchased).length} ${t('shopping.purchased', 'purchased')}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        {showActions && (
          <Box 
            display="flex" 
            gap={1} 
            flexDirection={{ xs: 'column', sm: 'row' }} 
          >
            {editable && onAddItem && (
              <Button
                variant="outlined"
                size="small"
                onClick={onAddItem}
                startIcon={<AddIcon />}
              >
                {t('shopping.addItem', 'Add Item')}
              </Button>
            )}
            {onSaveList && (
              <Button
                variant="contained"
                color="success"
                onClick={onSaveList}
                disabled={loading || items.length === 0}
                startIcon={<SaveIcon />}
              >
                {t('shopping.saveList', 'Save List')}
              </Button>
            )}
            {onCancel && (
              <Button
                variant="text"
                onClick={onCancel}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Info Alert for Generated List */}
      {isGeneratedList && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('shopping.editInstructions', 'Review and edit items before saving. You can add, remove, or modify quantities.')}
          </Alert>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {!isGeneratedList && <Divider />}

      {/* Items List - Grouped by Category */}
      <Box 
        sx={{ 
          bgcolor: isGeneratedList ? 'white' : 'transparent',
          borderRadius: isGeneratedList ? 1 : 0
        }}
      >
        {items.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography color="text.secondary">
              {loading 
                ? t('common.messages.loading', 'Loading...') 
                : (emptyMessage || t('shopping.empty', 'No items'))}
            </Typography>
          </Box>
        ) : (
          orderedCategories.map((category, categoryIndex) => {
            const categoryItems = groupedItems[category];
            const categoryItemCount = categoryItems.length;
            const purchasedCount = categoryItems.filter(item => item.is_purchased).length;
            
            return (
              <Box key={category}>
                {/* Category Header */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: '#f5f5f5',
                    borderTop: categoryIndex > 0 ? '1px solid #e0e0e0' : 'none',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="bold" 
                      color="primary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {category}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip 
                        label={`${categoryItemCount} ${t('shopping.items', 'items')}`}
                        size="small"
                        variant="outlined"
                      />
                      {purchasedCount > 0 && (
                        <Chip 
                          label={`${purchasedCount}/${categoryItemCount}`}
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Category Items */}
                <List sx={{ p: 0 }}>
                  {categoryItems.map((item) => {
                    // Find original index for proper deletion
                    const originalIndex = items.findIndex(i => 
                      (i.id && item.id && i.id === item.id) || 
                      (i.ingredient_name === item.ingredient_name && i.category === item.category)
                    );
                    
                    return (
                      <GroceryListItem
                        key={item.id || `${category}-${item.ingredient_name}`}
                        item={item}
                        index={originalIndex}
                        onEdit={editable ? onEditItem : null}
                        onDelete={editable ? onDeleteItem : null}
                        onTogglePurchased={onTogglePurchased}
                        editable={editable}
                        showActions={editable}
                      />
                    );
                  })}
                </List>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
};

export default GroceryListContainer;
